<template>
	<PopoverGroup class="hidden lg:flex gap-4 items-center">
		<template v-for="node in menu" :key="node.id">
			<Popover v-if="node.children.length">
				<PopoverButton
					:id="`popover-button-${node.id}`"
					v-slot="{ open }"
					class="flex items-center gap-1 text-xs uppercase text-current outline-none"
					:class="
						headerMode === 'transparent'
							? 'hover:border-white'
							: 'hover:border-primary'
					"
				>
					<span
						class="lg:border-b-[0.5px] lg:border-b-transparent lg:hover:border-primary"
						:class="
							isActiveRoute(node)
								? 'router-link-exact-active nav-link-dropdown'
								: ''
						"
					>
						{{ node.name }}
					</span>
					<ChevronDownIcon
						v-if="!open"
						class="h-5 w-5 flex-none text-current relative -top-[2px]"
						aria-hidden="true"
					/>
					<ChevronUpIcon
						v-if="open"
						class="h-5 w-5 flex-none text-current relative -top-[2px]"
						aria-hidden="true"
					/>
				</PopoverButton>

				<transition
					enter-active-class="transition ease-out duration-600"
					enter-from-class="opacity-0 translate-y-1"
					enter-to-class="opacity-100 translate-y-0"
					leave-active-class="transition ease-in duration-300"
					leave-from-class="opacity-100 translate-y-0"
					leave-to-class="opacity-0 translate-y-1"
				>
					<PopoverPanel
						:id="`navigation-panel-${node.id}`"
						v-slot="{ close }"
						class="dropdown absolute bottom-0 left-0 translate translate-y-full w-full z-50 overflow-hidden text-primary bg-tertiary shadow-lg border border-gray"
					>
						<div class="pt-6 pb-4 px-12">
							<NuxtLink :to="resolveHref(node)" @click="close">
								{{ $t("View all") }} {{ node.name }}
							</NuxtLink>
						</div>
						<div class="container flex justify-between gap-8 pb-12 px-12">
							<div class="grid grid-cols-5 gap-8 w-full">
								<div
									v-for="childNode in node.children"
									:key="childNode.id"
									class="group min-w-[218px] relative flex leading-6"
								>
									<div class="flex-auto block">
										<NuxtLink
											:to="resolveHref(childNode)"
											class="font-medium"
											@click="close"
											>{{ childNode.name }}
										</NuxtLink>
										<ul class="flex flex-col gap-4 mt-4">
											<li
												v-for="subChildNode in childNode.children"
												:key="subChildNode.id"
												class="flex flex-col flex-grow"
											>
												<NuxtLink
													:to="resolveHref(subChildNode)"
													class="!border-none"
													@click="close"
												>
													{{ subChildNode.name }}
												</NuxtLink>
											</li>
										</ul>
									</div>
								</div>
							</div>
							<div v-if="node.imageUrl" class="flex gap-8 justify-end">
								<NuxtLink :to="resolveHref(node)" @click="close">
									<NuxtImg
										:src="node.imageUrl"
										:alt="node.name"
										class="w-80"
										width="320"
										height="320"
										loading="lazy"
									/>
								</NuxtLink>
							</div>
						</div>
					</PopoverPanel>
				</transition>
			</Popover>

			<!-- Leaf: category without children, or a story/external link (old MenuLink look). -->
			<NuxtLink
				v-else
				:to="resolveHref(node)"
				class="nav-link text-xs uppercase lg:border-b-[0.5px] lg:border-b-transparent lg:hover:border-primary"
				:class="node.type === 'category' ? '' : 'nav-link-cms tracking-[0.9px]'"
			>
				{{ node.name }}
			</NuxtLink>
		</template>
	</PopoverGroup>
</template>

<script setup lang="ts">
import {
	Popover,
	PopoverButton,
	PopoverGroup,
	PopoverPanel
} from "@headlessui/vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid";
import type { ResolvedMenuNode } from "~/types/storyblok/storyblok";
import { isMenuNodeActive } from "~/templates/breadandboxers/utils/headerMenu";

interface Props {
	headerMode: string;
	menu: ResolvedMenuNode[];
}

const { headerMode, menu } = defineProps<Props>();

const route = useRoute();
const { resolveMenuHref: resolveHref } = useStoryblokLink();

// Matched on the localized href from the first path segment, so /herr is not
// active on /herrskor and a top-level /strumpor not on /herr/strumpor.
const isActiveRoute = (node: ResolvedMenuNode): boolean =>
	isMenuNodeActive(node, route.path, resolveHref);
</script>
